import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Upload, FolderPlus, Check, X, Image, Trash2, FolderOpen,
  ChevronRight, Loader2, Plus, Grid3X3, List
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return { Authorization: `Bearer ${token}` };
};

export default function MediaLibraryPopup({ open, onClose, onSelect, multiple = false }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [dragOver, setDragOver] = useState(false);

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const url = activeFolder
        ? `${API}/media-library?folder=${encodeURIComponent(activeFolder)}`
        : `${API}/media-library`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setFolders(data.folders || []);
      }
    } catch {
      toast.error("Failed to load media library");
    }
    setLoading(false);
  }, [activeFolder]);

  useEffect(() => {
    if (open) fetchLibrary();
  }, [open, fetchLibrary]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((file, i) => formData.append(`file${i}`, file));
    formData.append("folder", activeFolder || "Uploads");
    try {
      const res = await fetch(`${API}/media-library/upload`, {
        method: "POST",
        headers: getHeaders(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Uploaded ${data.count} file(s)`);
        fetchLibrary();
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const toggleSelect = (mediaId) => {
    const next = new Set(selected);
    if (next.has(mediaId)) next.delete(mediaId);
    else {
      if (!multiple) next.clear();
      next.add(mediaId);
    }
    setSelected(next);
  };

  const handleConfirm = () => {
    const selectedItems = items.filter((item) => selected.has(item.media_id));
    if (selectedItems.length === 0) {
      toast.error("Select at least one image");
      return;
    }
    if (multiple) {
      onSelect(selectedItems.map((i) => i.url));
    } else {
      onSelect(selectedItems[0].url);
    }
    onClose();
  };

  const handleDelete = async (mediaId) => {
    try {
      const res = await fetch(`${API}/media-library/${mediaId}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (res.ok) {
        setItems((prev) => prev.filter((i) => i.media_id !== mediaId));
        selected.delete(mediaId);
        setSelected(new Set(selected));
        toast.success("Deleted");
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await fetch(`${API}/media-library/folder`, {
        method: "POST",
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ name: newFolderName.trim() }),
      });
      setFolders((prev) => [...new Set([...prev, newFolderName.trim()])]);
      setActiveFolder(newFolderName.trim());
      setNewFolderName("");
      setShowNewFolder(false);
    } catch {
      toast.error("Failed to create folder");
    }
  };

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-semibold" data-testid="media-library-title">Media Library</DialogTitle>
            <div className="flex items-center gap-2">
              <button onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="media-view-toggle">
                {viewMode === "grid" ? <List className="w-4 h-4 text-gray-500" /> : <Grid3X3 className="w-4 h-4 text-gray-500" />}
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar - Folders */}
          <div className="w-44 border-r bg-gray-50/50 shrink-0 overflow-y-auto">
            <div className="p-2">
              <button
                onClick={() => setActiveFolder(null)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                  !activeFolder ? "bg-white shadow-sm text-gray-900" : "text-gray-600 hover:bg-white/80"
                }`}
                data-testid="media-folder-all"
              >
                <Image className="w-3.5 h-3.5" /> All Media
              </button>
              {folders.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFolder(f)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                    activeFolder === f ? "bg-white shadow-sm text-gray-900" : "text-gray-600 hover:bg-white/80"
                  }`}
                  data-testid={`media-folder-${f}`}
                >
                  <FolderOpen className="w-3.5 h-3.5" /> {f}
                </button>
              ))}

              {showNewFolder ? (
                <div className="flex items-center gap-1 mt-1 px-1">
                  <Input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name"
                    className="h-7 text-xs"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                    data-testid="media-new-folder-input"
                  />
                  <button onClick={handleCreateFolder} className="p-1 rounded hover:bg-green-50 text-green-600"><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); }} className="p-1 rounded hover:bg-red-50 text-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewFolder(true)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-colors mt-1"
                  data-testid="media-new-folder-btn"
                >
                  <FolderPlus className="w-3.5 h-3.5" /> New Folder
                </button>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Drop zone / Upload */}
            <div
              className={`mx-4 mt-3 mb-2 border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer ${
                dragOver ? "border-purple-400 bg-purple-50" : "border-gray-200 hover:border-gray-300"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById("media-file-input").click()}
              data-testid="media-dropzone"
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                  <span className="text-xs text-gray-500">Uploading...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500">Drop files here or click to upload</span>
                </div>
              )}
              <input
                id="media-file-input"
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
                data-testid="media-file-input"
              />
            </div>

            {/* Breadcrumb */}
            {activeFolder && (
              <div className="px-4 pb-1 flex items-center text-xs text-gray-400">
                <button onClick={() => setActiveFolder(null)} className="hover:text-gray-600">All</button>
                <ChevronRight className="w-3 h-3 mx-1" />
                <span className="text-gray-700 font-medium">{activeFolder}</span>
              </div>
            )}

            {/* Gallery */}
            <div className="flex-1 overflow-y-auto px-4 pb-3">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Image className="w-8 h-8 mb-2" />
                  <p className="text-sm">No images yet</p>
                  <p className="text-xs">Upload files to get started</p>
                </div>
              ) : viewMode === "grid" ? (
                <div className="grid grid-cols-4 gap-2">
                  {items.map((item) => (
                    <div
                      key={item.media_id}
                      onClick={() => toggleSelect(item.media_id)}
                      className={`relative group rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                        selected.has(item.media_id) ? "border-purple-500 ring-2 ring-purple-200" : "border-transparent hover:border-gray-200"
                      }`}
                      data-testid={`media-item-${item.media_id}`}
                    >
                      <img src={resolveUrl(item.url)} alt={item.original_name} className="w-full h-24 object-cover bg-gray-100" />
                      {selected.has(item.media_id) && (
                        <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.media_id); }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                        data-testid={`media-delete-${item.media_id}`}
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                      <p className="text-[10px] text-gray-500 px-1 py-0.5 truncate">{item.original_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {items.map((item) => (
                    <div
                      key={item.media_id}
                      onClick={() => toggleSelect(item.media_id)}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selected.has(item.media_id) ? "bg-purple-50 border border-purple-200" : "hover:bg-gray-50 border border-transparent"
                      }`}
                      data-testid={`media-item-${item.media_id}`}
                    >
                      <img src={resolveUrl(item.url)} alt={item.original_name} className="w-10 h-10 rounded object-cover bg-gray-100" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{item.original_name}</p>
                        <p className="text-[10px] text-gray-400">{item.folder} - {new Date(item.uploaded_at).toLocaleDateString()}</p>
                      </div>
                      {selected.has(item.media_id) && <Check className="w-4 h-4 text-purple-500 shrink-0" />}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.media_id); }}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between shrink-0 bg-gray-50/50">
          <p className="text-xs text-gray-400">
            {selected.size > 0 ? `${selected.size} selected` : `${items.length} items`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="media-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="bg-black text-white hover:bg-gray-800"
              data-testid="media-select-btn"
            >
              {selected.size > 0 ? `Use ${selected.size} Image${selected.size > 1 ? "s" : ""}` : "Select"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
